import { useEffect, useRef, useState } from "react";
import { ArrowDown, CircleCheck, Download, PenLine } from "lucide-react";
import { Badge, Button, Card, Checkbox, Input, Label } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, pdfHref, type PortalContractDetail } from "../../../lib/portal";
import { fmtDate, fmtAmount, genericStatusVariant } from "../../../lib/format";
import { DocLayout, DocSkeleton, Notice, PageHead } from "../../ui";
import { contentHtml } from "./contentHtml";
import { SignaturePad } from "./SignaturePad";

/** The look of an operator-written contract text: quiet, readable long-form. */
const PROSE =
  "max-w-prose text-[15px] leading-relaxed [&_a]:text-primary [&_a]:underline [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:font-semibold [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-4 [&_strong]:font-semibold [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0";

/**
 * One contract, as a page: the text on the left, its facts and the signature
 * on the right.
 *
 * On a phone the text comes FIRST and everything else after it, which is why
 * this page fills the layout's `rail` and leaves its `lead` empty. An invoice's
 * amount is the answer a reader came for; a contract's signature is something
 * they give after reading, and a signing form above the text would ask for it
 * before.
 *
 * `signable` is the server's answer. The form asks for a name and a consent
 * tick; the drawn signature is optional, as it always was.
 */
export function ContractPage({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [detail, setDetail] = useState<PortalContractDetail | null>(null);
  const [signerName, setSignerName] = useState("");
  const [consent, setConsent] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(false);
  const signRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    // An id this reader may not open answers 404; the list is the answer.
    void portal.contract(id).then((d) => { if (live) setDetail(d); }).catch(() => { if (live) onBack(); });
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function sign() {
    if (!detail || signerName.trim().length < 2 || !consent) return;
    setSigning(true);
    setError(false);
    try {
      const updated = await portal.signContract(detail.id, signerName.trim(), signatureImage ?? undefined);
      setDetail(updated);
    } catch {
      setError(true);
    } finally {
      setSigning(false);
    }
  }

  if (!detail) return <DocSkeleton label={t("common.loading")} />;

  const html = contentHtml(detail.content);
  const pdf = pdfHref(`/portal/contracts/${detail.id}/pdf`);

  const facts: { label: string; value: string }[] = [];
  if (detail.number) facts.push({ label: t("portal.contracts.number"), value: detail.number });
  if (detail.type) facts.push({ label: t("portal.contracts.type"), value: detail.type });
  if (detail.valueCents != null) {
    facts.push({ label: t("portal.contracts.value"), value: fmtAmount(detail.valueCents, detail.currency, locale) });
  }
  if (detail.startDate) facts.push({ label: t("portal.contracts.start"), value: fmtDate(detail.startDate, locale) });
  if (detail.endDate) facts.push({ label: t("portal.contracts.end"), value: fmtDate(detail.endDate, locale) });
  if (detail.signedAt) facts.push({ label: t("portal.contracts.signed"), value: fmtDate(detail.signedAt, locale) });

  return (
    <div>
      <PageHead
        back={{ label: t("portal.contracts.back"), onClick: onBack }}
        title={detail.title}
        badge={
          detail.signable ? (
            <Badge>{t("portal.contracts.needsSignature")}</Badge>
          ) : (
            <Badge variant={genericStatusVariant(detail.status)}>{t(`portal.contractStatus.${detail.status}`)}</Badge>
          )
        }
        description={detail.client.name}
        actions={
          // Only where the signing form is out of sight: under `lg` it sits
          // below the whole text, and a long contract hides it completely.
          detail.signable && (
            <Button
              variant="outline"
              className="lg:hidden"
              onClick={() => signRef.current?.scrollIntoView({ block: "start", behavior: "smooth" })}
            >
              <ArrowDown />
              {t("portal.contracts.goSign")}
            </Button>
          )
        }
      />

      <DocLayout
        paper={
          <Card className="p-5 md:p-8">
            {html ? (
              // Sanitized in `contentHtml` (DOMPurify) before it gets here.
              <div className={PROSE} dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p className="text-sm text-foreground/70">{t("portal.contracts.noText")}</p>
            )}
          </Card>
        }
        rail={
          <>
            {facts.length > 0 && (
              <Card className="p-6">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm lg:grid-cols-1 lg:gap-y-3">
                  {facts.map((f) => (
                    <div key={f.label} className="min-w-0 lg:flex lg:items-baseline lg:justify-between lg:gap-4">
                      <dt className="text-foreground/70">{f.label}</dt>
                      <dd className="font-semibold tabular-nums lg:text-right">{f.value}</dd>
                    </div>
                  ))}
                </dl>
                <Button asChild variant="outline" className="mt-5 w-full">
                  <a href={pdf} target="_blank" rel="noreferrer">
                    <Download />
                    {t("portal.contracts.downloadPdf")}
                  </a>
                </Button>
              </Card>
            )}

            {detail.signature && (
              <Notice icon={CircleCheck} tone="success">
                <p className="font-semibold">{t("portal.contracts.signedNotice")}</p>
                <p className="text-xs text-foreground/70">
                  {detail.signature.name}
                  {detail.signature.signedAt && <> · {fmtDate(detail.signature.signedAt, locale)}</>}
                </p>
                {detail.signature.image && (
                  // White in both themes: it is the signature as it was drawn
                  // on the white pad - see `SignaturePad`.
                  <img
                    src={detail.signature.image}
                    alt={t("portal.contracts.signatureAlt")}
                    className="mt-3 h-20 w-auto max-w-60 rounded-md border border-border bg-white p-1"
                  />
                )}
              </Notice>
            )}

            {detail.signable && (
              <Card ref={signRef} className="scroll-mt-24 space-y-4 border-primary/40 p-6">
                <div>
                  <h2 className="font-bold tracking-tight">{t("portal.contracts.signTitle")}</h2>
                  <p className="mt-1 text-pretty text-sm text-foreground/70">{t("portal.contracts.signIntro")}</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contract-signer">{t("portal.contracts.signerName")}</Label>
                  <Input
                    id="contract-signer"
                    autoComplete="name"
                    maxLength={120}
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder={t("portal.contracts.signerPlaceholder")}
                  />
                </div>
                <SignaturePad
                  value={signatureImage}
                  onChange={setSignatureImage}
                  label={t("portal.contracts.drawSignature")}
                  hint={t("portal.contracts.drawHint")}
                  clearLabel={t("portal.contracts.clearSignature")}
                />
                <label className="flex items-start gap-2.5 text-sm">
                  <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
                  <span>{t("portal.contracts.consent")}</span>
                </label>
                {error && <p role="alert" className="text-sm text-destructive">{t("portal.contracts.signError")}</p>}
                <Button
                  size="lg"
                  className="w-full"
                  loading={signing}
                  disabled={signerName.trim().length < 2 || !consent}
                  onClick={() => void sign()}
                >
                  <PenLine />
                  {t("portal.contracts.signButton")}
                </Button>
              </Card>
            )}

            {/* A contract with no facts at all still needs its PDF somewhere. */}
            {facts.length === 0 && (
              <Button asChild variant="outline" className="w-full">
                <a href={pdf} target="_blank" rel="noreferrer">
                  <Download />
                  {t("portal.contracts.downloadPdf")}
                </a>
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}
